import { useState, useEffect } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { getPlatformSettings, updatePlatformSettings } from '../utils/api';
import { useAppSettings } from '../stores/appSettings';

export function BillingPage(): React.JSX.Element {
  const { tg, initData } = useTelegram();
  const { currentRole } = useAppSettings();
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({
    pro: 2990,
    ai_extra: 990
  });

  const [subStatus, setSubStatus] = useState<{ plan: string, expiresAt: number | null }>({ plan: 'FREE', expiresAt: null });

  useEffect(() => {
    async function load() {
      if (currentRole === 'admin') {
        const res = await getPlatformSettings(initData);
        if (res.ok) setPrices(res.value);
      } else if (currentRole === 'owner') {
        const { getBillingStatus } = await import('../utils/api');
        const res = await getBillingStatus(initData);
        if (res.ok) setSubStatus(res.value);
      }
      setLoading(false);
    }
    load();
  }, [initData, currentRole]);

  const handleSave = async () => {
    const res = await updatePlatformSettings(initData, prices);
    if (res.ok) {
      tg?.HapticFeedback.notificationOccurred('success');
      tg?.showAlert('Цены на платформе обновлены! ✅');
    } else {
      tg?.showAlert('Ошибка сохранения: ' + res.error.message);
    }
  };

  const handlePay = async () => {
    setLoading(true);
    const { payBilling } = await import('../utils/api');
    const res = await payBilling(initData, 'PRO');
    if (res.ok) {
      setSubStatus(res.value.status);
      tg?.HapticFeedback.notificationOccurred('success');
      tg?.showAlert('Подписка успешно продлена! ✅');
    } else {
      tg?.showAlert('Ошибка оплаты: ' + res.error?.message);
    }
    setLoading(false);
  };

  if (loading) return <div className="p-10 text-center">Загрузка данных...</div>;

  // --- ЭКРАН ВЛАДЕЛЬЦА (Управление своей подпиской) ---
  if (currentRole === 'owner') {
    const isPro = subStatus.plan === 'PRO';
    const expireDate = subStatus.expiresAt ? new Date(subStatus.expiresAt).toLocaleDateString('ru-RU') : '-';
    const daysLeft = subStatus.expiresAt ? Math.max(0, Math.ceil((subStatus.expiresAt - Date.now()) / (1000 * 60 * 60 * 24))) : 0;

    return (
      <div className="p-1">
        <h1>💎 Моя подписка</h1>
        
        <div className="card" style={{ 
          background: isPro ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' : 'var(--tg-secondary-bg)', 
          color: isPro ? 'black' : 'var(--tg-text)', 
          marginBottom: '25px' 
        }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', opacity: 0.8 }}>Текущий план</div>
          <div style={{ fontSize: '32px', fontWeight: '900', margin: '5px 0' }}>{isPro ? 'PRO Plan' : 'FREE'}</div>
          {isPro && <div style={{ fontSize: '12px' }}>Действует до: {expireDate} (Осталось {daysLeft} дней)</div>}
          {!isPro && <div style={{ fontSize: '12px' }}>Базовые функции бесплатно</div>}
        </div>

        <div className="section-title">История оплат</div>
        <div className="booking-item">
          <div className="booking-info">
            <h4>{isPro ? 'Продление подписки PRO' : 'Нет оплат'}</h4>
            {isPro && <p>Карта **** 4242</p>}
          </div>
          {isPro && <div className="time-val">2,990 ₽</div>}
        </div>

        <button 
          onClick={handlePay}
          className="card" 
          style={{ width: '100%', backgroundColor: 'var(--tg-btn)', color: 'white', marginTop: '20px', cursor: 'pointer' }}
        >
          <span className="card-label" style={{ color: 'white', textAlign: 'center', width: '100%' }}>
            {isPro ? 'Продлить подписку' : 'Перейти на PRO'}
          </span>
        </button>
      </div>
    );
  }

  // --- ЭКРАН СУПЕР-АДМИНА (Управление тарифами платформы) ---
  return (
    <div className="p-1">
      <h1>💎 Тарифы и цены</h1>

      <div className="section-title">Стоимость подписок (₽)</div>

      <div className="card" style={{ marginBottom: '20px', gap: '15px' }}>
        <div>
          <label className="status-label">PRO План (в месяц)</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input 
              type="number" 
              className="card-value" 
              style={{ background: 'none', border: 'none', color: 'var(--tg-text)', width: '120px', outline: 'none' }}
              value={prices.pro}
              onChange={(e) => setPrices({...prices, pro: Number(e.target.value)})}
            />
            <span style={{ fontWeight: '800' }}>₽</span>
          </div>
        </div>

        <div>
          <label className="status-label">AI Ассистент (доп. опция)</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input 
              type="number" 
              className="card-value" 
              style={{ background: 'none', border: 'none', color: 'var(--tg-text)', width: '120px', outline: 'none' }}
              value={prices.ai_extra}
              onChange={(e) => setPrices({...prices, ai_extra: Number(e.target.value)})}
            />
            <span style={{ fontWeight: '800' }}>₽</span>
          </div>
        </div>
      </div>

      <button 
        onClick={handleSave}
        className="card" 
        style={{ width: '100%', backgroundColor: 'var(--tg-btn)', color: 'white', alignItems: 'center', cursor: 'pointer' }}
      >
        <span className="card-label" style={{ color: 'white' }}>Сохранить новые цены</span>
      </button>

      <div className="section-title" style={{ marginTop: '30px' }}>Активные акции</div>
      <div className="booking-item">
        <div className="booking-info">
          <h4>Летний старт</h4>
          <p>Скидка 50% на первый месяц</p>
        </div>
        <div className="status-badge status-confirmed">Активна</div>
      </div>
    </div>
  );
}
